import {Promise} from "axios";
import mdAxios from "../../core/common/config/axios-config";
import {observable} from "mobx";
import {ProjectVersion} from "../domain/project/projectVersionDomain";
import {ProjectVersionParamValue} from "../domain/project/projectVersionParameterValueDomain";
import {IDomainObject} from "../../core/form/formUtils";
import {ProjectCriterionVersion} from "../domain/project/projectCriterionVersionDomain";
import {ProjectVersionMaterial} from "../domain/project/projectVersionMaterialDomain";
import {Criterion} from "../domain/criterion/criterionDomain";
import {ProjectVersionEnergyCarrier} from "../domain/project/projectVersionEnergyCarrierDomain";
import {ProjectVersionRoom} from "../domain/project/projectVersionRoomDomain";
import {ProjectVersionArea} from "../domain/project/projectVersionAreaDomain";
import {ProjectVersionWindow} from "../domain/project/projectVersionWindowDomain";
import ProjectVersionLop from "../domain/project/projectVersionLopDomain";
import ProjectVersionTree from "../domain/project/projectVersionTreeDomain";
import ProjectVersionLopFillings from "../domain/project/projectVersionLopFillingDomain";
import ProjectVersionLopProfiles from "../domain/project/projectVersionLopProfileDomain";
import ProjectVersionParking from "../domain/project/projectVersionParkingDomain";
import {ProjectVersionRunoffCoefficient} from "../domain/project/projectRunoffCoefficientDomain";
import {ProjectVersionCapacity} from "../domain/project/projectVersionCapacitiesDomain";

export interface ProjectVersionResults {
  [criterionCode : string] : number;
}

class ProjectVersionService {
  @observable public projectVersion : ProjectVersion;
  @observable private _criterionResults : ProjectVersionResults;

  readonly criteriaList = observable<Criterion>([])
  readonly criterionVersions = observable<ProjectCriterionVersion>([])


  get criterionResults(): ProjectVersionResults {
    return this._criterionResults ? this._criterionResults : {};
  }

  set criterionResults(value: ProjectVersionResults) {
    this._criterionResults = value;
  }

  readProjectVersion(id: number): Promise<ProjectVersion> {
    return mdAxios.get('sbtool/project-version/' + id)
      .then(response => new ProjectVersion(response.data));
  }

  readProjectsVersionCriteria(id: number): Promise<Criterion[]> {
    return mdAxios.get('sbtool/project-version/' + id + "/criteria")
      .then(response => response.data.map(d => new Criterion(d)));
  }

  saveProjectVersion(projectVersionObject: ProjectVersion) : Promise<IDomainObject> {
    return mdAxios.post('sbtool/project-version', projectVersionObject)
      .then((response) => response.data);
  }

  copyProjectVersion(oldVersionId : number, projectVersionObject: ProjectVersion) : Promise<IDomainObject> {
    return mdAxios.post('sbtool/project-version/' + oldVersionId + '/copy', projectVersionObject)
      .then((response) => response.data);
  }

  deleteProjectVersion(id : number) : Promise<any> {
    return mdAxios.delete('sbtool/project-version/' + id, {showPrettyError: true})
  }

  readProjectVersionValues(projectVersionId : number, criterionId : number, versionId : number) : Promise<ProjectVersionParamValue[]> {
    return mdAxios.get('sbtool/project-version/' + projectVersionId + "/values/" + criterionId + "/" + versionId)
      .then(response => response.data.map(param => new ProjectVersionParamValue(param).initAfterRead(false))
      );
  }

  saveProjectVersionValues(projectVersionId : number, criterionId : number, criterionVersionId : number , newName : string, params : ProjectVersionParamValue[]) : Promise<void> {
    let url = 'sbtool/project-version/' + projectVersionId + "/values/" + criterionId + "/" + criterionVersionId;
    if (newName) {
      url += '/newName/' + newName;
    }
    return mdAxios.post(url, params)
      .then(response => {});
  }

  getProjectVersionResults(projectVersionId : number) : Promise<ProjectVersionResults> {
    return mdAxios.get('sbtool/project-version/' + projectVersionId + "/results")
      .then(response => response.data);
  }

  readProjectCriteriaVersions(projectVersionId : number) : Promise<ProjectCriterionVersion[]> {
    return mdAxios.get('sbtool/project-version/' + projectVersionId + "/criterion-versions")
      .then(response => response.data.map(param => new ProjectCriterionVersion(param).initAfterRead(false))
      );
  }

  copyProjectCriterionVersion(projectVersionId : number, versionId : number, copy: ProjectCriterionVersion) : Promise<ProjectCriterionVersion> {
    return mdAxios.post('sbtool/project-version/' + projectVersionId + "/criterion-versions/" + versionId, copy)
      .then(response => new ProjectCriterionVersion(response.data).initAfterRead(false));
  }

  setDefaultCriterionVersion(projectVersionId : number, versionId : number) : Promise<void> {
    return mdAxios.post('sbtool/project-version/' + projectVersionId + "/criterion-versions/" + versionId + "/default")
      .then(response => {});
  }

  deleteCriterionVersion(projectVersionId : number, versionId : number) : Promise<void> {
    return mdAxios.delete('sbtool/project-version/' + projectVersionId + "/criterion-versions/" + versionId)
      .then(response => {});
  }

  saveProjectVersionMaterial(projectVersionId: number, material : ProjectVersionMaterial): Promise<IDomainObject> {
    return mdAxios.post('sbtool/project-version/' + projectVersionId + "/materials", material)
      .then((response) => response.data);
  }
  readProjectVersionMaterial(materialId : number): Promise<ProjectVersionMaterial> {
    return mdAxios.get("sbtool/project-version/materials/" + materialId)
      .then(response => new ProjectVersionMaterial(response.data));
  }
  deleteProjectVersionMaterial(materialId : number): Promise<void> {
    return mdAxios.delete("sbtool/project-version/materials/" + materialId)
      .then(response => {});
  }

  saveProjectVersionEnergyCarrier(projectVersionId: number, energyCarrier : ProjectVersionEnergyCarrier): Promise<IDomainObject> {
    return mdAxios.post('sbtool/project-version/' + projectVersionId + "/energy-carriers", energyCarrier)
      .then((response) => response.data);
  }
  readProjectVersionMaterialEnergyCarrier(energyCarrierId : number): Promise<ProjectVersionEnergyCarrier> {
    return mdAxios.get("sbtool/project-version/energy-carriers/" + energyCarrierId)
      .then(response => new ProjectVersionEnergyCarrier(response.data));
  }
  deleteProjectVersionEnergyCarrier(materialId : number): Promise<void> {
    return mdAxios.delete("sbtool/project-version/energy-carriers/" + materialId)
      .then(response => {});
  }
  roomTypesSearch(): Promise<{options: ProjectVersionRoom[]}> {
    return mdAxios.get('sbtool/project-version/roomTypes')
      .then(response => {
        return {options : response.data.map(d => new ProjectVersionRoom(d))}
      });
  }
  saveProjectVersionRoom(projectVersionId: number, room : ProjectVersionRoom): Promise<IDomainObject> {
    return mdAxios.post('sbtool/project-version/' + projectVersionId + "/rooms", room)
      .then((response) => response.data);
  }
  deleteProjectVersionRoom(roomId : number): Promise<void> {
    return mdAxios.delete("sbtool/project-version/rooms/" + roomId)
      .then(response => {});
  }

  saveProjectVersionArea(projectVersionId: number, area : ProjectVersionArea): Promise<IDomainObject> {
    return mdAxios.post('sbtool/project-version/' + projectVersionId + "/areas", area)
      .then((response) => response.data);
  }
  deleteProjectVersionArea(areaId : number): Promise<void> {
    return mdAxios.delete("sbtool/project-version/areas/" + areaId)
      .then(response => {});
  }

  saveProjectVersionWindow(projectVersionId: number, window : ProjectVersionWindow): Promise<IDomainObject> {
    return mdAxios.post('sbtool/project-version/' + projectVersionId + "/windows", window)
      .then((response) => response.data);
  }

  deleteProjectVersionWindow(windowId : number): Promise<void> {
    return mdAxios.delete("sbtool/project-version/windows/" + windowId)
      .then(response => {});
  }

  getProjectVersionAreaBySubCriterionCode(projectVersionId: number, subCriterionCode : string) : Promise<any> {
    return mdAxios.get(`/sbtool/project-version/areas?projectVersionId=${projectVersionId}&subCriterionCode=${subCriterionCode}`)
      .then(response => response.data);
  }

  saveProjectVersionLop(projectVersionId: number, lop : ProjectVersionLop) : Promise<IDomainObject> {
    return mdAxios.post('sbtool/project-version/' + projectVersionId + '/lops', lop)
      .then((response) => response.data);
  }

  deleteProjectVersionLop(lopId : number) : Promise<void> {
    return mdAxios.delete("/sbtool/project-version/lops/" + lopId)
      .then(response => {});
  }

  saveProjectVersionTree(projectVersionId: number, tree : ProjectVersionTree) : Promise<IDomainObject> {
    return mdAxios.post('sbtool/project-version/' + projectVersionId + '/trees', tree)
      .then((response) => response.data);
  }

  deleteProjectVersionTree(treeId : number) : Promise<void> {
    return mdAxios.delete("/sbtool/project-version/trees/" + treeId)
      .then(response => {});
  }
  getVersionResult(versionId: number): Promise<any> {
    return mdAxios.get('sbtool/project/' + versionId + '/results')
      .then(response => response.data);
  }

  saveProjectVersionLopsFillings(projectVersionId: number, lop : ProjectVersionLopFillings) : Promise<IDomainObject> {
    return mdAxios.post('sbtool/project-version/' + projectVersionId + '/lopfillings', lop)
      .then((response) => response.data);
  }

  deleteProjectVersionLopsFillings(lopId : number) : Promise<void> {
    return mdAxios.delete("/sbtool/project-version/lopfillings/" + lopId)
      .then(response => {});
  }
  saveProjectVersionLopsProfiles(projectVersionId: number, lop : ProjectVersionLopProfiles) : Promise<IDomainObject> {
    return mdAxios.post('sbtool/project-version/' + projectVersionId + '/lopprofiles', lop)
      .then((response) => response.data);
  }

  deleteProjectVersionLopsProfiles(lopId : number) : Promise<void> {
    return mdAxios.delete("/sbtool/project-version/lopprofiles/" + lopId)
      .then(response => {});
  }
  saveProjectVersionParking(projectVersionId: number, lop : ProjectVersionParking) : Promise<IDomainObject> {
    return mdAxios.post('sbtool/project-version/' + projectVersionId + '/parkings', lop)
      .then((response) => response.data);
  }

  deleteProjectVersionParking(lopId : number) : Promise<void> {
    return mdAxios.delete("/sbtool/project-version/parkings/" + lopId)
      .then(response => {});
  }
  saveProjectVersionRunoffCoefficient(projectVersionId: number, runoffCoefficient : ProjectVersionRunoffCoefficient) : Promise<IDomainObject> {
    return mdAxios.post('sbtool/project-version/' + projectVersionId + '/runoff-coefficient', runoffCoefficient)
      .then((response) => response.data);
  }

  deleteProjectVersionRunoffCoefficient(coefficient : number) : Promise<void> {
    return mdAxios.delete("/sbtool/project-version/runoff-coefficient/" + coefficient)
      .then(response => {});
  }
  saveProjectVersionIdentification(projectVersionId: number, capacity : ProjectVersionCapacity) : Promise<IDomainObject> {
    return mdAxios.post('sbtool/project-version/' + projectVersionId + '/identification', capacity)
      .then((response) => response.data);
  }

  deleteProjectVersionIdentification(projectVersionId : number) : Promise<void> {
    return mdAxios.delete("/sbtool/project-version/identification/" + projectVersionId)
      .then(response => {});
  }

  finishProject(projectVersionId: number): Promise<any> {
    return mdAxios.get('sbtool/project-version/' + projectVersionId + '/close')
      .then((response) => response.data);
  }

  downloadCertificate(projectVersionId: number): Promise<any> {
    return mdAxios.get('sbtool/project-version/download-certificate/'+projectVersionId, {
      responseType: 'blob',
    })
      .then((response) => response);
  }
}

export default new ProjectVersionService();



// WEBPACK FOOTER //
// ./src/sbtool/service/projectVersionService.ts